import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  clearLocalStorage,
  getLocalStorage,
  removeItemFromLocalStorage,
  setCookie,
  setLocalStorage,
  showToastMessage,
} from "../../utils";
import {
  APPLICATION_ACCESS,
  ROLES,
  TOAST_POSITION,
  TOAST_TYPE,
} from "../../constants";
import {
  adminLogin,
  getUserDetails,
  getPermission,
} from "../actions/admin/authAction";
import {
  ADMIN_ROUTES,
  AGENT_ROUTES,
  BUILDER_ROUTES,
  SALES_ROUTES,
  USER_ROUTES,
} from "../../constants/appRoutes";
import { customerLogin } from "../actions/customer/authAction";

interface UserInfo {
  _id: string;
  email: string;
  full_name: string;
  contact_number: null;
  created_at: string;
  [key: string]: any;
}

export interface IAuthState {
  loading: boolean;
  userInfo: UserInfo | any;
  accessToken: string | null;
  error: any;
}

const initialState: IAuthState = {
  loading: false,
  userInfo: null,
  accessToken: null,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      removeItemFromLocalStorage("accessToken");
      removeItemFromLocalStorage("refreshToken");
      removeItemFromLocalStorage("userRole");
      removeItemFromLocalStorage("sellerType");
      removeItemFromLocalStorage("userId");
      state.loading = false;
      state.userInfo = null;
      state.accessToken = null;
      state.error = null;
      if (
        window.location.pathname.includes(ADMIN_ROUTES.ROOT) ||
        window.location.pathname.includes(AGENT_ROUTES.ROOT) ||
        window.location.pathname.includes(BUILDER_ROUTES.ROOT) ||
        window.location.pathname.includes(SALES_ROUTES.ROOT)
      ) {
        window.location.href = ADMIN_ROUTES.LOGIN;
      } else {
        window.location.href = USER_ROUTES.ROOT;
      }
    },
    updateUserInfo: (state, action) => {
      state.userInfo = { ...state.userInfo, ...action.payload?.userInfo };
      state.accessToken = action.payload?.accessToken;
      state.loading = action.payload?.loading ?? false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(adminLogin.fulfilled, (state, action: PayloadAction<any>) => {
        const { data } = action.payload;
        setLocalStorage("accessToken", data?.token);
        setLocalStorage("refreshToken", data?.refreshToken);
        setLocalStorage("userId", data?.user?._id);
        if (data?.user?.user_role === 1) {
          setLocalStorage("userRole", ROLES.ADMIN);
        } else if (data?.user?.user_role === 6) {
          setLocalStorage("userRole", ROLES.STAFF);
        } else if (data?.user?.user_role === 2) {
          setLocalStorage("userRole", ROLES.AGENT);
        } else if (data?.user?.user_role === 3) {
          setLocalStorage("userRole", ROLES.BUILDER);
        } else if (data?.user?.user_role === 7) {
          setLocalStorage("userRole", ROLES.SALES);
        }
        showToastMessage(TOAST_TYPE.SUCCESS, data?.message);
        state.loading = false;
        state.error = null;
        state.userInfo = data.user;
        state.accessToken = data.token;
      })
      .addCase(adminLogin.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.userInfo = null;
        state.accessToken = null;
        state.error = action.payload || "An error occurred";
        showToastMessage(
          TOAST_TYPE.ERROR,
          action.payload || "An error occurred",
          { position: TOAST_POSITION.TOP_CENTER }
        );
      });

    builder
      .addCase(getUserDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserDetails.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.userInfo = { ...state.userInfo, ...payload?.data };
        state.accessToken = getLocalStorage("accessToken");
      })
      .addCase(getUserDetails.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        clearLocalStorage();
        state.userInfo = null;
        state.accessToken = null;
        state.error = action.payload;
      });

    builder
      .addCase(getPermission.fulfilled, (state, { payload }) => {
        state.userInfo = {
          ...state.userInfo,
          permissions: payload?.data?.list,
        };
      })
      .addCase(getPermission.rejected, (_, action: PayloadAction<any>) => {
        showToastMessage(
          TOAST_TYPE.ERROR,
          action.payload || "An error occurred",
          { position: TOAST_POSITION.TOP_CENTER }
        );
      });

    builder
      .addCase(customerLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(customerLogin.fulfilled, (state, action: PayloadAction<any>) => {
        const { data } = action.payload;
        const applicationAccess: any = APPLICATION_ACCESS;
        const userRole = data?.user?.user_role;

        const useApplicationRole = (() => {
          if (userRole === 5) {
            const sellerType = data?.user?.seller_type;
            return applicationAccess[5]?.[sellerType];
          } else {
            return applicationAccess[userRole];
          }
        })();

        if (userRole === 5) {
          setLocalStorage("sellerType", useApplicationRole?.access);
        }

        setLocalStorage("accessToken", data.token);
        setLocalStorage("refreshToken", data.refreshToken);
        setCookie("accessToken", data.token);
        setCookie("refreshToken", data.refreshToken);
        setLocalStorage("userId", data?.user?._id);
        setLocalStorage("userRole", useApplicationRole?.type);

        showToastMessage(TOAST_TYPE.SUCCESS, data.message);
        state.loading = false;
        state.error = null;
        state.userInfo = data?.user;
        state.accessToken = data?.token;
      })
      .addCase(customerLogin.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.userInfo = null;
        state.accessToken = null;
        state.error = action.payload || "An error occurred";
        showToastMessage(
          TOAST_TYPE.ERROR,
          action.payload?.message || "An error occurred"
        );
      });
  },
});

export const { logout, updateUserInfo } = authSlice.actions;
export default authSlice.reducer;
